import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, AlertCircle } from 'lucide-react';
import { formatSimpleDate } from '../utils/dateFormatter';
import { supabase } from '../supabaseClient';

const CalendarView = ({ problems, onTaskClick }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month', 'team'
  const [subtasks, setSubtasks] = useState([]);
  const [dayTableExpanded, setDayTableExpanded] = useState(false);
  const [teamTableExpanded, setTeamTableExpanded] = useState(false);

  // Fetch all subtasks
  useEffect(() => {
    const fetchSubtasks = async () => {
      try {
        const { data, error } = await supabase
          .from('subtasks')
          .select('*')
          .order('due_date', { ascending: true });

        if (error) throw error;
        setSubtasks(data || []);
      } catch (error) {
        console.error('Error fetching subtasks:', error);
      }
    };

    fetchSubtasks();
  }, []);

  // Get subtasks for a specific task and date
  const getSubtasksForTaskAndDate = (taskId, dateStr) => {
    return subtasks.filter(st => {
      if (st.problem_id !== taskId || !st.due_date) return false;
      const subtaskDateStr = new Date(st.due_date).toISOString().split('T')[0];
      return subtaskDateStr === dateStr;
    });
  };

  // Get today's date at midnight for comparison
  const getTodayMidnight = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Get tasks with deadlines (excluding Done tasks and subtasks from flattened list)
  const tasksWithDeadlines = useMemo(() => {
    return problems.filter(p => p.due_date && p.status !== 'Done' && p.type === 'problem');
  }, [problems]);

  // Get tasks for selected date
  const tasksForSelectedDate = useMemo(() => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    return tasksWithDeadlines.filter(p => {
      if (!p.due_date) return false;
      const taskDateStr = new Date(p.due_date).toISOString().split('T')[0];
      return taskDateStr === selectedDateStr;
    });
  }, [tasksWithDeadlines, selectedDate]);

  // Get tasks due today
  const tasksToday = useMemo(() => {
    const today = getTodayMidnight();
    const todayStr = today.toISOString().split('T')[0];
    return tasksWithDeadlines.filter(p => {
      const taskDateStr = new Date(p.due_date).toISOString().split('T')[0];
      return taskDateStr === todayStr;
    });
  }, [tasksWithDeadlines]);

  // Get subtasks for the selected date with their parent task info
  const subtasksForSelectedDate = useMemo(() => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];

    return subtasks
      .filter(st => {
        if (!st.due_date) return false;
        const subtaskDateStr = new Date(st.due_date).toISOString().split('T')[0];
        return subtaskDateStr === selectedDateStr;
      })
      .map(st => {
        // Find parent task - only match against main tasks (type: 'problem')
        const parentTask = problems.find(p => p.id === st.problem_id && p.type === 'problem');
        return {
          ...st,
          parentTask: parentTask || null
        };
      })
      .filter(st => st.parentTask); // Only include subtasks with valid parent tasks
  }, [subtasks, problems, selectedDate]);

  // Get main tasks for selected date that don't have any subtasks
  const mainTasksWithoutSubtasks = useMemo(() => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];

    return tasksWithDeadlines.filter(task => {
      // Check if task is due on selected date
      const taskDateStr = new Date(task.due_date).toISOString().split('T')[0];
      if (taskDateStr !== selectedDateStr) return false;

      // Check if this task has any subtasks
      const hasSubtasks = subtasks.some(st => st.problem_id === task.id);
      return !hasSubtasks; // Only include if no subtasks exist
    });
  }, [tasksWithDeadlines, subtasks, selectedDate]);

  // Calculate countdown (from 6:30 PM office closing time)
  const getCountdown = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);

    // Set time to 6:30 PM (18:30)
    due.setHours(18, 30, 0, 0);

    const diff = due - now;

    if (diff < 0) {
      const hours = Math.abs(Math.floor(diff / (1000 * 60 * 60)));
      const days = Math.floor(hours / 24);
      if (days > 0) return `${days} day${days > 1 ? 's' : ''} overdue`;
      return `${hours} h overdue`;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} h remaining`;
    return `${minutes} min remaining`;
  };

  // Check if overdue (only after today ends at 11:59 PM)
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const due = new Date(dueDate);
    due.setHours(23, 59, 59, 999);
    return due < today;
  };

  // Navigate dates
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Get week days starting from Sunday
  const getWeekDays = () => {
    const start = new Date(selectedDate);
    const day = start.getDay();
    start.setDate(start.getDate() - day);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Get calendar month grid
  const getMonthGrid = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }

    // Add current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Add next month days to complete the grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return tasksWithDeadlines.filter(p => {
      const taskDate = new Date(p.due_date);
      return taskDate.getFullYear() === year &&
        taskDate.getMonth() === month &&
        taskDate.getDate() === day;
    });
  };

  // Get subtasks for a specific date with parent task info
  const getSubtasksForDate = (date) => {
    // Normalize the date to avoid timezone issues
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return subtasks
      .filter(st => {
        if (!st.due_date) return false;
        const subtaskDate = new Date(st.due_date);
        return subtaskDate.getFullYear() === year &&
          subtaskDate.getMonth() === month &&
          subtaskDate.getDate() === day;
      })
      .map(st => {
        // Find parent task - only match against main tasks (type: 'problem')
        const parentTask = problems.find(p => p.id === st.problem_id && p.type === 'problem');
        return {
          ...st,
          parentTask: parentTask || null
        };
      })
      .filter(st => st.parentTask);
  };

  // Get main tasks without subtasks for a specific date
  const getMainTasksWithoutSubtasksForDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return tasksWithDeadlines.filter(task => {
      // Check if task is due on the date
      const taskDate = new Date(task.due_date);
      if (taskDate.getFullYear() !== year ||
        taskDate.getMonth() !== month ||
        taskDate.getDate() !== day) return false;

      // Check if this task has any subtasks
      const hasSubtasks = subtasks.some(st => st.problem_id === task.id);
      return !hasSubtasks; // Only include if no subtasks exist
    });
  };

  // Get priority style
  const getPriorityStyle = (priority) => {
    const p = priority?.toLowerCase() || '';
    switch (p) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  // Helper for Status Color
  const getStatusColorClass = (status, isCompleted) => {
    if (isCompleted) return 'text-green-400';
    const s = status?.toLowerCase();
    if (s === 'hold' || s === 'blocked') return 'text-yellow-400';
    return 'text-blue-400';
  };

  // Format time from due date (defaults to office closing time)
  const getTimeFromDate = (date) => {
    // Default to office closing time (6:30 PM) for all tasks
    return '6:30 PM';
  };

  // Unified flat list: tasks-with-subtasks expand into subtask rows; tasks-without-subtasks stay as-is
  const calendarItems = useMemo(() => {
    const items = [];

    // Add one row per subtask (for tasks that have subtasks)
    subtasks.forEach(st => {
      const parentTask = problems.find(p => p.id === st.problem_id && p.type === 'problem');
      if (!parentTask || parentTask.status === 'Done' || !st.due_date) return;
      items.push({
        id: `sub-${st.id}`,
        title: st.title,
        domain: parentTask.domain,
        status: parentTask.status,
        priority: parentTask.priority,
        due_date: st.due_date,
        assigned_user: parentTask.assigned_user,
        isSubtask: true,
        originalTask: parentTask,
      });
    });

    // Add main tasks that have NO subtasks
    tasksWithDeadlines.forEach(task => {
      const hasSubtasks = subtasks.some(st => st.problem_id === task.id);
      if (!hasSubtasks) {
        items.push({
          id: `task-${task.id}`,
          title: task.title,
          domain: task.domain,
          status: task.status,
          priority: task.priority,
          due_date: task.due_date,
          assigned_user: task.assigned_user,
          isSubtask: false,
          originalTask: task,
        });
      }
    });

    return items;
  }, [subtasks, tasksWithDeadlines, problems]);

  // Group tasks by time for day view
  const tasksByTime = useMemo(() => {
    const grouped = {};
    tasksForSelectedDate.forEach(task => {
      const time = getTimeFromDate(task.due_date);
      if (!grouped[time]) grouped[time] = [];
      grouped[time].push(task);
    });
    return grouped;
  }, [tasksForSelectedDate]);

  return (
    <div className="space-y-6">
      {/* View Mode Tabs */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
        <div className="flex gap-2 bg-gray-900/50 p-1 rounded-lg overflow-x-auto max-w-full no-scrollbar">
          {['day', 'week', 'month', 'team'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md font-medium capitalize transition-colors text-sm whitespace-nowrap ${viewMode === mode
                ? 'bg-blue-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between w-full md:w-auto gap-2 md:gap-3 bg-gray-900/30 p-1 rounded-xl md:bg-transparent md:p-0">
          <button
            onClick={() => navigateDate(-1)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="text-base md:text-lg font-semibold flex-1 md:flex-initial md:min-w-[200px] text-center truncate px-2">
            {viewMode === 'day' && selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
            {viewMode === 'week' && (() => {
              const weekDays = getWeekDays();
              const start = weekDays[0];
              const end = weekDays[6];
              return `${start.getDate()} ${start.toLocaleDateString('en-US', { month: 'short' })} - ${end.getDate()} ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
            })()}
            {viewMode === 'month' && selectedDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric'
            })}
            {viewMode === 'team' && selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          <button
            onClick={() => navigateDate(1)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
          >
            <ChevronRight size={20} />
          </button>
          <button
            onClick={goToToday}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-gray-400 hover:text-white font-medium"
          >
            Today
          </button>
        </div>
      </div>

      {/* Deadline Today Section */}
      {viewMode === 'day' && (subtasksForSelectedDate.length > 0 || mainTasksWithoutSubtasks.length > 0) && (
        <div>
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <AlertCircle size={20} className="text-orange-400" />
            Deadline for {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </h3>
          <div className="space-y-2">
            {/* Display subtasks */}
            {subtasksForSelectedDate.map((subtask) => (
              <div
                key={`subtask-${subtask.id}`}
                onClick={() => onTaskClick(subtask.parentTask)}
                className="glass-card p-3 cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-white text-sm">
                      <span className="text-blue-400">@{subtask.parentTask.title}</span>
                      <span className="text-gray-400 mx-2">-</span>
                      <span className={subtask.is_completed ? 'line-through text-gray-500' : ''}>
                        {subtask.title}
                      </span>
                    </h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityStyle(subtask.parentTask.priority)}`}>
                      {subtask.parentTask.priority}
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {formatSimpleDate(subtask.due_date)}
                    </span>
                    <span className={`text-sm font-medium ${isOverdue(subtask.due_date) ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                      ({getCountdown(subtask.due_date)})
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Display main tasks without subtasks */}
            {mainTasksWithoutSubtasks.map((task) => (
              <div
                key={`task-${task.id}`}
                onClick={() => onTaskClick(task)}
                className="glass-card p-3 cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-white text-sm">
                      {task.title}
                    </h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityStyle(task.priority)}`}>
                      {task.priority}
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {formatSimpleDate(task.due_date)}
                    </span>
                    <span className={`text-sm font-medium ${isOverdue(task.due_date) ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                      ({getCountdown(task.due_date)})
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}



      {/* Week View - Weekly Grid */}
      {viewMode === 'week' && (
        <div className="flex flex-col md:grid md:grid-cols-7 gap-4">
          {getWeekDays().map((day, index) => {
            const daySubtasks = getSubtasksForDate(day);
            const dayMainTasks = getMainTasksWithoutSubtasksForDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            const totalItems = daySubtasks.length + dayMainTasks.length;

            return (
              <div key={index} className={`glass-card p-4 ${isToday ? 'border-2 border-blue-500' : ''}`}>
                <div className="mb-3 flex md:block items-center justify-between gap-4">
                  <div className="flex items-center gap-2 md:block">
                    <div className="text-sm md:text-xs text-gray-400 uppercase font-semibold">
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-xl md:text-2xl font-bold ${isToday ? 'text-blue-400' : 'text-white'}`}>
                      {day.getDate()}
                    </div>
                  </div>
                  {/* Mobile Task Count Badge */}
                  <div className="md:hidden">
                    <span className="text-xs bg-white/10 px-2 py-1 rounded-full text-gray-300">
                      {totalItems} tasks
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {totalItems > 0 ? (
                    <>
                      {/* Display subtasks */}
                      {daySubtasks.map((subtask) => {
                        const isHold = subtask.parentTask.status?.toLowerCase() === 'hold' || subtask.parentTask.status?.toLowerCase() === 'blocked';
                        return (
                          <div
                            key={`subtask-${subtask.id}`}
                            onClick={() => onTaskClick(subtask.parentTask)}
                            className={`p-2 rounded-lg cursor-pointer transition-colors group mb-1 ${
                              subtask.is_completed ? 'bg-green-500/10 hover:bg-green-500/20 text-green-400' :
                              isHold ? 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium truncate flex-1">
                                {subtask.title}
                              </span>
                            </div>
                            <div className="text-[10px] opacity-80 truncate">
                              @{subtask.parentTask.title}
                            </div>
                            <div className="text-[10px] opacity-60 capitalize truncate">
                              {subtask.parentTask.domain}
                            </div>
                          </div>
                        );
                      })}

                      {/* Display main tasks without subtasks */}
                      {dayMainTasks.map((task) => {
                        const isHold = task.status?.toLowerCase() === 'hold' || task.status?.toLowerCase() === 'blocked';
                        return (
                          <div
                            key={`task-${task.id}`}
                            onClick={() => onTaskClick(task)}
                            className={`p-2 rounded-lg cursor-pointer transition-colors group mb-1 ${
                              task.status === 'Done' ? 'bg-green-500/10 hover:bg-green-500/20 text-green-400' :
                              isHold ? 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium truncate flex-1">
                                {task.title}
                              </span>
                            </div>
                            <div className="text-xs opacity-60 capitalize truncate">
                              {task.domain}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-4 md:py-4">No tasks</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Month View - Calendar Grid */}
      {viewMode === 'month' && (
        <div>
          {/* Month Header */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
              <div key={day} className="text-center text-[10px] md:text-xs font-medium text-gray-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-7 gap-2">
            {getMonthGrid().map((day, index) => {
              const daySubtasks = getSubtasksForDate(day.date);
              const dayMainTasks = getMainTasksWithoutSubtasksForDate(day.date);
              const isToday = day.date.toDateString() === new Date().toDateString();
              const allItems = [...daySubtasks, ...dayMainTasks];

              return (
                <div
                  key={index}
                  onClick={() => setSelectedDate(day.date)}
                  className={`min-h-[60px] md:min-h-[120px] p-1 md:p-2 rounded-lg border transition-all cursor-pointer md:cursor-default relative ${day.isCurrentMonth
                    ? 'glass-card border-white/10'
                    : 'bg-white/5 border-white/5 opacity-50'
                    } ${isToday ? 'border-blue-500 ring-1 ring-blue-500/50' : ''} ${selectedDate.toDateString() === day.date.toDateString() ? 'bg-blue-500/10 border-blue-500/50' : ''}`}
                >
                  <div className={`text-sm font-medium mb-2 ${day.isCurrentMonth
                    ? isToday ? 'text-blue-400' : 'text-white'
                    : 'text-gray-600'
                    }`}>
                    {day.date.getDate()}
                  </div>


                  {/* Mobile Dot Indicators */}
                  <div className="md:hidden mt-2 flex flex-wrap gap-1 justify-center">
                    {allItems.length > 0 && allItems.slice(0, 4).map((_, i) => (
                      <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < daySubtasks.length
                        ? subtasksForSelectedDate[i]?.is_completed ? 'bg-green-500' : 'bg-blue-500'
                        : 'bg-indigo-500'
                        }`} />
                    ))}
                    {allItems.length > 4 && <span className="text-[8px] text-gray-500 leading-none">+</span>}
                  </div>

                  {/* Desktop Full Details */}
                  <div className="hidden md:block space-y-1">
                    {/* Display all subtasks */}
                    {daySubtasks.map((subtask) => {
                      const isHold = subtask.parentTask.status?.toLowerCase() === 'hold' || subtask.parentTask.status?.toLowerCase() === 'blocked';
                      return (
                        <div
                          key={`subtask-${subtask.id}`}
                          onClick={(e) => { e.stopPropagation(); onTaskClick(subtask.parentTask); }}
                          className={`p-1 rounded text-xs cursor-pointer transition-colors ${subtask.is_completed ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' :
                            isHold ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400' :
                              'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                            }`}
                        >
                          <div className="flex items-center gap-1">
                            <span className="truncate flex-1">{subtask.title}</span>
                          </div>
                          <div className="text-[10px] opacity-80 truncate mt-0.5">
                            @{subtask.parentTask.title}
                          </div>
                        </div>
                      );
                    })}

                    {/* Display all main tasks without subtasks */}
                    {dayMainTasks.map((task) => {
                      const isHold = task.status?.toLowerCase() === 'hold' || task.status?.toLowerCase() === 'blocked';
                      return (
                        <div
                          key={`task-${task.id}`}
                          onClick={(e) => { e.stopPropagation(); onTaskClick(task); }}
                          className={`p-1 rounded text-xs cursor-pointer transition-colors ${task.status === 'Done' ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' :
                            isHold ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400' :
                              'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                            }`}
                        >
                          <div className="flex items-center gap-1">
                            <span className="truncate">{task.title}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Month View Details Section - Shows selected day's tasks below grid */}
      {viewMode === 'month' && (
        <div className="md:hidden mt-6 pb-6 border-t border-white/10 pt-4">
          <h3 className="text-lg font-semibold mb-3 text-white">
            {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </h3>

          <div className="space-y-2">
            {(() => {
              const daySubtasks = getSubtasksForDate(selectedDate);
              const dayMainTasks = getMainTasksWithoutSubtasksForDate(selectedDate);

              if (daySubtasks.length === 0 && dayMainTasks.length === 0) {
                return <p className="text-gray-500 text-sm italic">No tasks for this day</p>;
              }

              return (
                <>
                  {daySubtasks.map((subtask) => (
                    <div
                      key={`mobile-sub-${subtask.id}`}
                      onClick={() => onTaskClick(subtask.parentTask)}
                      className="glass-card p-3 cursor-pointer hover:bg-white/10 active:scale-[0.98] transition-all"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white text-sm truncate">
                            {subtask.title}
                          </h4>
                          <p className="text-xs text-blue-400 mt-0.5 truncate">
                            @{subtask.parentTask.title}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${subtask.is_completed ? 'bg-green-400' :
                            isOverdue(subtask.due_date) ? 'bg-red-400' : 'bg-blue-400'
                          }`} />
                      </div>
                    </div>
                  ))}
                  {dayMainTasks.map((task) => (
                    <div
                      key={`mobile-main-${task.id}`}
                      onClick={() => onTaskClick(task)}
                      className="glass-card p-3 cursor-pointer hover:bg-white/10 active:scale-[0.98] transition-all"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white text-sm truncate">
                            {task.title}
                          </h4>
                          <p className="text-xs text-gray-400 mt-0.5 capitalize">
                            {task.domain}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${task.status === 'Done' ? 'bg-green-400' :
                            isOverdue(task.due_date) ? 'bg-red-400' : 'bg-blue-400'
                          }`} />
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Team View */}
      {viewMode === 'team' && (
        <div className="space-y-6">
          {/* Team Tasks Table */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">
                Team Tasks <span className="text-gray-400 text-sm">({calendarItems.length})</span>
              </h3>
              {calendarItems.length > 5 && (
                <button
                  onClick={() => setTeamTableExpanded(v => !v)}
                  className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  {teamTableExpanded ? 'Show less ↑' : `Show all ${calendarItems.length} ↓`}
                </button>
              )}
            </div>
          <div className="glass-card overflow-hidden">
            <div className={teamTableExpanded ? 'overflow-y-auto max-h-[420px]' : ''}>
            <table className="w-full">
              <thead className="sticky top-0 bg-[#1a1f2e] z-10">
                <tr className="border-b border-white/10">
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Name</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Title</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Active / Due / Overdue</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Remaining Time or Overdue Time</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Sort items (subtask-expanded): Today first, then Overdue, then Active, Unassigned last
                  const sortedTasks = [...calendarItems].sort((a, b) => {
                    const aIsInactive = a.assigned_user?.is_active === false;
                    const aUserName = aIsInactive && a.assigned_user?.inactive_user_name 
                      ? a.assigned_user.inactive_user_name 
                      : (a.assigned_user?.name || 'Unassigned');
                    const bIsInactive = b.assigned_user?.is_active === false;
                    const bUserName = bIsInactive && b.assigned_user?.inactive_user_name 
                      ? b.assigned_user.inactive_user_name 
                      : (b.assigned_user?.name || 'Unassigned');
                    const aIsUnassigned = aUserName === 'Unassigned';
                    const bIsUnassigned = bUserName === 'Unassigned';

                    // Put unassigned tasks at the end
                    if (aIsUnassigned && !bIsUnassigned) return 1;
                    if (!aIsUnassigned && bIsUnassigned) return -1;

                    const todayStr = new Date().toISOString().split('T')[0];
                    const aIsToday = a.due_date && new Date(a.due_date).toISOString().split('T')[0] === todayStr;
                    const bIsToday = b.due_date && new Date(b.due_date).toISOString().split('T')[0] === todayStr;
                    const aIsOverdue = isOverdue(a.due_date);
                    const bIsOverdue = isOverdue(b.due_date);

                    // Today's tasks first
                    if (aIsToday && !bIsToday) return -1;
                    if (!aIsToday && bIsToday) return 1;

                    // Then overdue tasks
                    if (aIsOverdue && !bIsOverdue) return -1;
                    if (!aIsOverdue && bIsOverdue) return 1;

                    // Keep original order for same category
                    return 0;
                  });

                  const visibleItems = teamTableExpanded ? sortedTasks : sortedTasks.slice(0, 5);
                  return visibleItems.map((item) => {
                    const isInactive = item.assigned_user?.is_active === false;
                    const userName = isInactive && item.assigned_user?.inactive_user_name 
                      ? item.assigned_user.inactive_user_name 
                      : (item.assigned_user?.name || 'Unassigned');
                    const taskIsOverdue = isOverdue(item.due_date);
                    const countdown = getCountdown(item.due_date);

                    return (
                      <tr
                        key={item.id}
                        onClick={() => onTaskClick(item.originalTask)}
                        className="border-b border-white/10 hover:bg-white/5 cursor-pointer transition-colors"
                      >
                        <td className="px-3 py-2">
                          <span className="font-medium text-white text-xs">{userName}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-white text-xs">{item.title}</span>
                          {item.isSubtask && (
                            <div className="text-[10px] text-gray-500 mt-0.5">@{item.originalTask.title}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${taskIsOverdue
                            ? 'text-red-400'
                            : 'text-green-400'
                            }`}>
                            {taskIsOverdue ? 'Overdue' : 'Active'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs font-medium ${taskIsOverdue ? 'text-red-400' : 'text-yellow-400'
                            }`}>
                            {countdown}
                          </span>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            </div>
            {calendarItems.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm">
                No tasks with deadlines found
              </div>
            )}
            {!teamTableExpanded && calendarItems.length > 5 && (
              <div className="px-3 py-2 border-t border-white/10 text-center">
                <button
                  onClick={() => setTeamTableExpanded(true)}
                  className="text-xs text-gray-500 hover:text-indigo-400 transition-colors"
                >
                  + {calendarItems.length - 5} more tasks
                </button>
              </div>
            )}
          </div>
          </div>

          {/* Team Members Grid */}
          <div>
            {(() => {
              // Group tasks by assigned user name
              const tasksByMember = {};
              tasksWithDeadlines.forEach(task => {
                const isInactive = task.assigned_user?.is_active === false;
                const member = isInactive && task.assigned_user?.inactive_user_name 
                  ? task.assigned_user.inactive_user_name 
                  : (task.assigned_user?.name || 'Unassigned');
                if (!tasksByMember[member]) {
                  tasksByMember[member] = [];
                }
                tasksByMember[member].push(task);
              });

              // Sort members alphabetically
              const members = Object.keys(tasksByMember).sort();

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {members.map((member) => {
                    const memberTasks = tasksByMember[member];
                    return (
                      <div key={member} className="glass-card p-4">
                        <h4 className="font-semibold text-white mb-4 text-lg">{member}</h4>
                        <div className="space-y-2">
                          {memberTasks.map((task) => (
                            <div
                              key={task.id}
                              onClick={() => onTaskClick(task)}
                              className="p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-2 h-2 rounded-full ${task.priority === 'critical' ? 'bg-red-400' :
                                  task.priority === 'high' ? 'bg-orange-400' :
                                    task.priority === 'medium' ? 'bg-yellow-400' : 'bg-green-400'
                                  }`} />
                                <span className="text-sm font-medium text-white truncate flex-1">
                                  {task.title}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 truncate mb-1">
                                {task.domain} • {task.status}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatSimpleDate(task.due_date)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Tasks Table - Only shown in Day view as requested */}
      {viewMode === 'day' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold">
              Tasks <span className="text-gray-400 text-sm">({calendarItems.length} tasks)</span>
            </h3>
            {calendarItems.length > 5 && (
              <button
                onClick={() => setDayTableExpanded(v => !v)}
                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                {dayTableExpanded ? 'Show less ↑' : `Show all ${calendarItems.length} ↓`}
              </button>
            )}
          </div>
          <div className="glass-card overflow-hidden">
            <div className={dayTableExpanded ? 'overflow-y-auto max-h-[420px]' : ''}>
              <table className="w-full">
                <thead className="sticky top-0 bg-[#1a1f2e] z-10">
                  <tr className="border-b border-white/10">
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Task</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Domain</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Status</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Priority</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Deadline</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Countdown</th>
                  </tr>
                </thead>
                <tbody>
                  {(dayTableExpanded ? calendarItems : calendarItems.slice(0, 5)).map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => onTaskClick(item.originalTask)}
                      className="border-b border-white/10 hover:bg-white/5 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2">
                        <span className="font-medium text-white text-xs">{item.title}</span>
                        {item.isSubtask && (
                          <div className="text-[10px] text-gray-500 mt-0.5">@{item.originalTask.title}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-300 capitalize text-xs">{item.domain}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-300 capitalize text-xs">{item.status}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${getPriorityStyle(item.priority)}`}>
                          {item.priority}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-xs">
                        {formatSimpleDate(item.due_date)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-medium ${isOverdue(item.due_date) ? 'text-red-400' : 'text-yellow-400'}`}>
                          {getCountdown(item.due_date)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {calendarItems.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm">
                No tasks with deadlines found
              </div>
            )}
            {!dayTableExpanded && calendarItems.length > 5 && (
              <div className="px-3 py-2 border-t border-white/10 text-center">
                <button
                  onClick={() => setDayTableExpanded(true)}
                  className="text-xs text-gray-500 hover:text-indigo-400 transition-colors"
                >
                  + {calendarItems.length - 5} more tasks
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
